'use client';

import { CaretUp } from 'assets/icons';
import { forwardRef, useRef, useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Container } from 'components';
import { Hero } from './HeroCatSection';
import { ButtonRows, ButtonTabs } from './StyledConstants';
import styles from './PartnerCategories.module.css';

export const PartnerCategoriesMap = {
  RESEARCH: 'RESEARCH',
  HEALTHCARE: 'HEALTHCARE',
  SPORTS: 'SPORTS',
  FITNESS: 'FITNESS',
  COMPANIES: 'COMPANIES',
};

type PartnersTranslator = ReturnType<typeof useTranslations<'partners'>>;

function getPartnerCategories(t: PartnersTranslator): {
  [key: string]: { title: string; children?: React.ReactNode };
} {
  return {
    [PartnerCategoriesMap.RESEARCH]: {
      title: t('categories.research.title'),
      children: (
        <>
          <Hero
            title={t('categories.research.heading')}
            rootClass="padding"
            categoryTitle={t('categories.research.title')}
            img={{
              desktopSrc: 'research.png',
              mobileSrc: 'research.png',
              alt: t('categories.research.imageAlt'),
              width: 4808,
              height: 2888,
              caption: t('categories.research.caption'),
              captiionAlignment: 'right',
            }}
            cta={{
              link: '/ring/buy',
              text: t('categories.cta'),
            }}
          />
        </>
      ),
    },
    [PartnerCategoriesMap.HEALTHCARE]: {
      title: t('categories.healthcare.title'),
      children: (
        <>
          <Hero
            title={t('categories.healthcare.heading')}
            rootClass="padding"
            categoryTitle={t('categories.healthcare.title')}
            img={{
              desktopSrc: 'healthcare.png',
              mobileSrc: 'healthcare.png',
              alt: t('categories.healthcare.imageAlt'),
              width: 4808,
              height: 2888,
              caption: t('categories.healthcare.caption'),
              captiionAlignment: 'right',
            }}
            cta={{
              link: '/ring/buy',
              text: t('categories.cta'),
            }}
          />
        </>
      ),
    },
    [PartnerCategoriesMap.SPORTS]: {
      title: t('categories.sports.title'),
      children: (
        <>
          <Hero
            title={t('categories.sports.heading')}
            rootClass="padding"
            categoryTitle={t('categories.sports.title')}
            img={{
              desktopSrc: 'sports.png',
              mobileSrc: 'sports.png',
              alt: t('categories.sports.title'),
              width: 4808,
              height: 2888,
              caption: t('categories.sports.caption'),
              captiionAlignment: 'right',
            }}
            cta={{
              link: '/ring/buy',
              text: t('categories.cta'),
            }}
          />
        </>
      ),
    },
    [PartnerCategoriesMap.FITNESS]: {
      title: t('categories.fitness.title'),
      children: (
        <>
          <Hero
            title={t('categories.fitness.heading')}
            rootClass="padding"
            categoryTitle={t('categories.fitness.title')}
            img={{
              desktopSrc: 'fitness.png',
              mobileSrc: 'fitness.png',
              alt: t('categories.fitness.title'),
              width: 4808,
              height: 2888,
              caption: t('categories.fitness.caption'),
              captiionAlignment: 'right',
            }}
            cta={{
              link: '/ring/buy',
              text: t('categories.cta'),
            }}
          />
        </>
      ),
    },
    [PartnerCategoriesMap.COMPANIES]: {
      title: t('categories.companies.title'),
      children: (
        <>
          <Hero
            title={t('categories.companies.heading')}
            rootClass="padding"
            categoryTitle={t('categories.companies.title')}
            img={{
              desktopSrc: 'company.png',
              mobileSrc: 'company.png',
              alt: t('categories.companies.title'),
              width: 4808,
              height: 2888,
              caption: t('categories.companies.caption'),
              captiionAlignment: 'right',
            }}
            cta={{
              link: '/ring/buy',
              text: t('categories.cta'),
            }}
          />
        </>
      ),
    },
  };
}

export const PartnerCategories = forwardRef(
  ({ className }: { className?: string }, ref: React.Ref<HTMLElement>) => {
    const t = useTranslations('partners');
    const partnerCategories = getPartnerCategories(t);
    const [active, setActive] = useState(
      () => Object.keys(partnerCategories)[0],
    );
    const [displayedContent, setDisplayedContent] = useState(
      () => Object.keys(partnerCategories)[0],
    );
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [showLeftCaret, setShowLeftCaret] = useState(false);
    const [showRightCaret, setShowRightCaret] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRowsRef = useRef<HTMLDivElement>(null);

    const checkOverflow = () => {
      if (buttonRowsRef.current) {
        const { scrollWidth, clientWidth, scrollLeft } = buttonRowsRef.current;
        setShowLeftCaret(scrollLeft > 0);
        setShowRightCaret(
          scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth,
        );
      }
    };

    useEffect(() => {
      checkOverflow();
      window.addEventListener('resize', checkOverflow);
      return () => window.removeEventListener('resize', checkOverflow);
    }, []);

    const scroll = (direction: 'left' | 'right') => {
      if (buttonRowsRef.current) {
        const scrollAmount = 200;
        buttonRowsRef.current.scrollBy({
          left: direction === 'left' ? -scrollAmount : scrollAmount,
          behavior: 'smooth',
        });
      }
    };

    const handleScroll = () => {
      checkOverflow();
    };

    return (
      <section
        ref={ref}
        className={`${styles.root}${className ? ` ${className}` : ''}`}
      >
        <Container ref={containerRef}>
          <div className={styles.buttonRowsWrapper}>
            {showLeftCaret && (
              <button
                className={`${styles.scrollButton} ${styles.left}`}
                onClick={() => scroll('left')}
                aria-label={t('categories.scrollLeft')}
              >
                <CaretUp />
              </button>
            )}
            <ButtonRows ref={buttonRowsRef} onScroll={handleScroll}>
              {Object.keys(partnerCategories).map((key) => (
                <ButtonTabs
                  className={key === active ? styles.active : ''}
                  key={key}
                  onClick={(e) => {
                    if (key !== active) {
                      setActive(key);
                      setIsTransitioning(true);
                      setTimeout(() => {
                        setDisplayedContent(key);
                        setTimeout(() => {
                          setIsTransitioning(false);
                        }, 50);
                      }, 300);
                    }
                    if (window.innerWidth > 769) return;
                    (e.target as HTMLButtonElement).scrollIntoView({
                      behavior: 'smooth',
                      inline: 'center',
                      block: 'start',
                    });
                  }}
                >
                  {partnerCategories[key].title}
                </ButtonTabs>
              ))}
            </ButtonRows>
            {showRightCaret && (
              <button
                className={`${styles.scrollButton} ${styles.right}`}
                onClick={() => scroll('right')}
                aria-label={t('categories.scrollRight')}
              >
                <CaretUp />
              </button>
            )}
          </div>
          <div
            className={`${styles.content} ${
              isTransitioning ? styles.fadeOut : styles.fadeIn
            }`}
          >
            {partnerCategories[displayedContent].children}
          </div>
          {/* Hidden preload containers */}
          <div style={{ display: 'none' }}>
            {Object.keys(partnerCategories).map((key) =>
              key !== displayedContent ? (
                <div key={key}>{partnerCategories[key].children}</div>
              ) : null,
            )}
          </div>
        </Container>
      </section>
    );
  },
);

PartnerCategories.displayName = 'PartnerCategories';
